export type ParsedWorkScope = {
  hasPrimer: boolean;
  hasPlaster: boolean;
  hasSanding: boolean;
  hasPainting: boolean;
  hasHydro: boolean;
  hasTiles: boolean;
  hasPanels: boolean;
  hasScraping: boolean;
  isApartment: boolean;
  roomCount?: number;
};

/**
 * Dlaczego fragment zakresu nie moze zostac wyceniony automatycznie.
 * `nierozpoznana_praca` - parser nie zna tej roboty.
 * `wymaga_wlasnej_stawki` - robota znana, ale rozliczana inaczej niz sciana
 *   za m2 (rury, grzejniki, listwy, panele w zakresie mieszanym).
 */
export type ManualScopeFragmentReason = 'nierozpoznana_praca' | 'wymaga_wlasnej_stawki';

export type ManualScopeFragment = {
  /** Oryginalny fragment tekstu wykonawcy, bez normalizacji. */
  text: string;
  reason: ManualScopeFragmentReason;
};

export type ParsedManualWorkScope = {
  scopeText: string;
  scopeLabel: string;
  work: {
    primer: boolean;
    plaster: boolean;
    sanding: boolean;
    painting: boolean;
    scraping: boolean;
  };
  hasPanels: boolean;
  hasScraping: boolean;
  hasKnownSupportedWork: boolean;
  needsManualConfirmation: boolean;
  confirmationReasons: string[];
  /**
   * Fragmenty, ktorych parser NIE policzyl. Musza zostac rozpisane recznie
   * w istniejacym ManualItemsEditor. Pusta lista = caly zakres rozpoznany.
   */
  unsupportedFragments: ManualScopeFragment[];
};

const POLISH_NUMBER_WORDS: Record<string, number> = {
  jeden: 1,
  jedno: 1,
  dwa: 2,
  dwie: 2,
  trzy: 3,
  cztery: 4,
  piec: 5,
  szesc: 6,
};

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[łŁ]/g, 'l')
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function parseWorkScope(text: string): ParsedWorkScope {
  const normalized = normalize(text);
  const roomNumberMatch = normalized.match(/\b(\d+)\s*(pokoj|pokoje|pokoi)\b/);
  const roomWordMatch = normalized.match(/\b(jeden|jedno|dwa|dwie|trzy|cztery|piec|szesc)\s*(pokoj|pokoje|pokoi)\b/);
  const roomCount = roomNumberMatch
    ? Number(roomNumberMatch[1])
    : roomWordMatch
      ? POLISH_NUMBER_WORDS[roomWordMatch[1] ?? '']
      : undefined;

  return {
    hasPrimer: /\b(grunt|gruntowanie|zagruntowac|surowe|surowy|deweloperskie|deweloperski)\b/.test(normalized),
    hasPlaster: /\b(gladz|gladzie|szpachla|szpachlowanie|gipsowanie)\b/.test(normalized),
    hasSanding: /\b(szlif|szlifowanie)\b/.test(normalized),
    hasPainting: /\b(malowanie|malowaniem|malarsk\w*|farba|farby|pomalowac|sciany|sufit|2x\s*malowanie)\b/.test(normalized),
    hasHydro: /\b(hydro|hydroizolacja|folia\s+w\s+plynie)\b/.test(normalized),
    hasTiles: /\b(plytki|gres|glazura)\b/.test(normalized),
    hasPanels: /\b(panele|paneli|podloga|podlogi)\b/.test(normalized),
    hasScraping: /\b(skrobanie|skrobac|zeskrobanie|zdzieranie|usuwanie\s+farby)\b/.test(normalized),
    isApartment: /\b(mieszkanie|lokal|cale\s+mieszkanie)\b/.test(normalized),
    roomCount,
  };
}

/**
 * Rozdziela zakres na fragmenty. Celowo NIE dzielimy po " i ", bo
 * "Gruntowanie i malowanie 50 m2" to jeden fragment opisujacy dwie prace.
 */
const SCOPE_FRAGMENT_SEPARATOR = /\s*[,;+/\n]\s*|\s+oraz\s+/i;

/** Panele/podloga: wlasny zakres z wlasna stawka, nie malowanie sciany. */
const PANEL_PATTERN = /\b(panel|panele|paneli|panelami|podloga|podlogi|podloge)\b/;

/**
 * Elementy rozliczane inaczej niz sciana za m2. Fragment z takim elementem
 * nigdy nie zasila flag pracy — inaczej "malowanie rur" zostaloby policzone
 * stawka malowania scian.
 */
const OWN_RATE_OBJECT_PATTERN =
  /\b(rura|rury|rur|rurach|rurki|grzejnik|grzejnika|grzejniki|grzejnikow|kaloryfer|kaloryfery|zebro|zebra|zeber|listwa|listwy|listew|listwami|cokol|cokoly|cokolow|mdf)\b/;

const PRIMER_PATTERN = /\b(grunt|gruntowanie|zagruntowac|zagruntowanie)\b/;
const PLASTER_PATTERN = /\b(gladz|gladzie|szpachla|szpachlowanie|gipsowanie)\b/;
const SANDING_PATTERN = /\b(szlif|szlifowanie)\b/;
const PAINTING_PATTERN = /\b(malowanie|malowaniem|malarsk\w*|farba|farby|pomalowac|2x\s*malowanie)\b/;
const SCRAPING_PATTERN = /\b(skrobanie|skrobac|zeskrobanie|zdzieranie|usuwanie\s+farby)\b/;

function splitScopeFragments(text: string): string[] {
  return text
    .split(SCOPE_FRAGMENT_SEPARATOR)
    .map((fragment) => fragment.trim())
    .filter((fragment) => fragment.length > 0);
}

function quoteFragments(fragments: ManualScopeFragment[]): string {
  return fragments.map((fragment) => `"${fragment.text}"`).join(', ');
}

export function parseManualWorkScope(text: string): ParsedManualWorkScope {
  const scopeText = text.trim();
  const fragments = splitScopeFragments(scopeText);

  // Flagi pracy powstaja WYLACZNIE z fragmentow, ktore parser potrafi wycenic
  // stawka scienna. Fragment z wlasna stawka (rury, grzejnik, listwy) albo
  // nierozpoznany nie zasila zadnej flagi — nie moze udawac, ze zostal policzony.
  let primer = false;
  let plaster = false;
  let sanding = false;
  let painting = false;
  let scraping = false;

  const unsupported: ManualScopeFragment[] = [];
  const panelFragments: ManualScopeFragment[] = [];

  for (const fragment of fragments) {
    const normalized = normalize(fragment);
    const isPanelFragment = PANEL_PATTERN.test(normalized);
    const needsOwnRate = isPanelFragment || OWN_RATE_OBJECT_PATTERN.test(normalized);

    if (needsOwnRate) {
      const entry: ManualScopeFragment = { text: fragment, reason: 'wymaga_wlasnej_stawki' };
      if (isPanelFragment) {
        panelFragments.push(entry);
      } else {
        unsupported.push(entry);
      }
      continue;
    }

    const fragmentPrimer = PRIMER_PATTERN.test(normalized);
    const fragmentPlaster = PLASTER_PATTERN.test(normalized);
    const fragmentSanding = SANDING_PATTERN.test(normalized);
    const fragmentPainting = PAINTING_PATTERN.test(normalized);
    const fragmentScraping = SCRAPING_PATTERN.test(normalized);

    if (
      !fragmentPrimer && !fragmentPlaster && !fragmentSanding &&
      !fragmentPainting && !fragmentScraping
    ) {
      unsupported.push({ text: fragment, reason: 'nierozpoznana_praca' });
      continue;
    }

    primer = primer || fragmentPrimer;
    plaster = plaster || fragmentPlaster;
    sanding = sanding || fragmentSanding;
    painting = painting || fragmentPainting;
    scraping = scraping || fragmentScraping;
  }

  const hasKnownSupportedWork = primer || plaster || sanding || painting || scraping;

  // Panele sa osobnym, wspieranym zakresem TYLKO wtedy, gdy stanowia caly zakres.
  // W zakresie mieszanym jeden metraz nie moze byc jednoczesnie sciana i podloga,
  // wiec panele ida do recznego rozpisania, a prace scienne licza sie normalnie.
  const isPanelsOnlyScope = panelFragments.length > 0 && !hasKnownSupportedWork;
  const hasPanels = isPanelsOnlyScope;
  const unsupportedFragments = isPanelsOnlyScope
    ? unsupported
    : [...unsupported, ...panelFragments];

  const labels = [
    scraping ? 'skrobanie' : undefined,
    primer ? 'gruntowanie' : undefined,
    plaster ? 'gladz' : undefined,
    sanding && !plaster ? 'szlifowanie' : undefined,
    painting ? 'malowanie' : undefined,
  ].filter(Boolean);

  const confirmationReasons = [
    ...(!hasKnownSupportedWork && !isPanelsOnlyScope ? ['zakres do potwierdzenia recznie'] : []),
    ...(isPanelsOnlyScope ? ['panele wymagaja wyceny w trybie szczegolowym'] : []),
    ...(unsupportedFragments.length > 0
      ? [`Do rozpisania ręcznie, nie policzone: ${quoteFragments(unsupportedFragments)}`]
      : []),
  ];

  return {
    scopeText,
    scopeLabel: labels.length > 0 ? labels.join(' + ') : 'do potwierdzenia',
    work: {
      primer,
      plaster,
      sanding: sanding || plaster,
      painting,
      scraping,
    },
    hasPanels,
    hasScraping: scraping,
    hasKnownSupportedWork,
    needsManualConfirmation: confirmationReasons.length > 0,
    confirmationReasons,
    unsupportedFragments,
  };
}
